import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, Btn, Select } from "@/components/erp/ui";
import { toast } from "sonner";
import { Workflow as WfIcon, Lock, Plus, Trash2, ArrowUp, ArrowDown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const inp = "border border-slate-300 rounded-lg px-2 py-1.5 text-sm min-w-0";

const NodeRow = ({ n, i, last, roles, dis, set, move, del }) => (
  <div className={`flex flex-wrap sm:flex-nowrap items-center gap-2 p-2 rounded-lg border ${n.active ? "border-slate-200" : "border-slate-100 bg-slate-50 opacity-60"}`} data-testid={`pn-row-${i}`}>
    <input type="color" disabled={dis} value={n.color} onChange={(e) => set({ color: e.target.value })} className="w-8 h-8 rounded shrink-0" data-testid={`pn-color-${i}`} />
    <input disabled={dis} value={n.label} onChange={(e) => set({ label: e.target.value })} className={`${inp} basis-[calc(100%-2.5rem)] sm:basis-auto flex-1`} data-testid={`pn-label-${i}`} />
    <select disabled={dis} value={n.default_role || ""} onChange={(e) => set({ default_role: e.target.value })} className={`${inp} flex-1 sm:flex-none sm:w-40`} data-testid={`pn-role-${i}`}>
      <option value="">— PIC role —</option>
      {roles.map((r) => <option key={r} value={r}>{r}</option>)}
    </select>
    <label className="flex items-center gap-1 text-xs text-slate-500 shrink-0"><input type="checkbox" disabled={dis} checked={n.active} onChange={(e) => set({ active: e.target.checked })} data-testid={`pn-active-${i}`} /> Aktif</label>
    {!dis && <span className="flex items-center shrink-0">
      <button type="button" disabled={i === 0} onClick={() => move(-1)} className="p-1 text-slate-400 disabled:opacity-30" data-testid={`pn-up-${i}`}><ArrowUp className="w-3.5 h-3.5" /></button>
      <button type="button" disabled={last} onClick={() => move(1)} className="p-1 text-slate-400 disabled:opacity-30" data-testid={`pn-down-${i}`}><ArrowDown className="w-3.5 h-3.5" /></button>
      <button type="button" onClick={del} className="p-1 text-slate-400 hover:text-rose-500" data-testid={`pn-del-${i}`}><Trash2 className="w-3.5 h-3.5" /></button>
    </span>}
  </div>
);

export const ProductionNodesSettings = () => {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [nodes, setNodes] = useState(null);
  const [roles, setRoles] = useState([]);
  const [wfs, setWfs] = useState([]);
  const [cats, setCats] = useState([]);
  const [map, setMap] = useState({ default_workflow_id: "", by_category: {} });
  useEffect(() => {
    api.get("/settings/production-nodes").then(({ data }) => setNodes(data)).catch(() => setNodes([]));
    api.get("/roles").then(({ data }) => setRoles((data || []).map((r) => r.key || r.name || r.id).filter(Boolean))).catch(() => {});
    api.get("/catalog/categories").then(({ data }) => setCats(data)).catch(() => {});
    api.get("/settings/workflow-mapping").then(({ data }) => setMap(data)).catch(() => {});
    api.get("/workflows-names").then(({ data }) => setWfs(data)).catch(() => {});
  }, []);
  if (!nodes) return <p className="text-sm text-slate-400">Memuat…</p>;
  const dis = !isOwner;
  const setN = (i, p) => setNodes(nodes.map((n, k) => (k === i ? { ...n, ...p } : n)));
  const move = (i, d) => { const a = [...nodes]; [a[i], a[i + d]] = [a[i + d], a[i]]; setNodes(a); };
  const save = async () => {
    try {
      const { data } = await api.put("/settings/production-nodes", { nodes });
      setNodes(data);
      await api.put("/settings/workflow-mapping", map);
      toast.success("Production Nodes & workflow default disimpan");
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan"); }
  };
  return (
    <div className="space-y-4" data-testid="production-nodes-settings">
      <Card className="p-5 space-y-3">
        <div className="flex items-center gap-2 font-semibold text-sm"><WfIcon className="w-4 h-4 text-sky-600" /> Production Nodes (palet Workflow Builder)</div>
        <p className="text-xs text-slate-500">Node aktif muncul di palet Workflow Builder. Nama node menjadi nama tahap di Work Order; PIC role menjadi role default tahap tersebut.</p>
        {dis && <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-center gap-1.5" data-testid="pn-owner-only"><Lock className="w-3.5 h-3.5" /> Hanya admin utama (owner) yang dapat mengubah pengaturan ini.</p>}
        <div className="space-y-1.5">
          {nodes.map((n, i) => <NodeRow key={n.id || i} n={n} i={i} last={i === nodes.length - 1} roles={roles} dis={dis} set={(p) => setN(i, p)} move={(d) => move(i, d)} del={() => setNodes(nodes.filter((_, k) => k !== i))} />)}
        </div>
        {!dis && <Btn variant="ghost" data-testid="pn-add" onClick={() => setNodes([...nodes, { label: `Node ${nodes.length + 1}`, color: "#64748b", active: true, default_role: "" }])}><Plus className="w-4 h-4" /> Tambah node</Btn>}
      </Card>
      <Card className="p-5 space-y-3">
        <div className="font-semibold text-sm">Workflow untuk WO dari Quotation</div>
        <p className="text-xs text-slate-500">Saat quotation di-approve menjadi WO: dipakai workflow kategori Katalog item pertama yang punya pengaturan → workflow Default → tahap standar (APPRAISAL … DELIVERY).</p>
        <label className="block text-xs text-slate-500">Workflow Default
          <Select disabled={dis} data-testid="wf-default" value={map.default_workflow_id} onChange={(e) => setMap({ ...map, default_workflow_id: e.target.value })}>
            <option value="">— tahap standar —</option>
            {wfs.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
          </Select>
        </label>
        <div className="grid sm:grid-cols-2 gap-2">
          {cats.map((c) => (
            <label key={c.id} className="block text-xs text-slate-500 min-w-0">{c.name}
              <Select disabled={dis} data-testid={`wf-cat-${c.id}`} value={map.by_category?.[c.id] || ""} onChange={(e) => setMap({ ...map, by_category: { ...map.by_category, [c.id]: e.target.value } })}>
                <option value="">— ikut Default —</option>
                {wfs.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
              </Select>
            </label>
          ))}
        </div>
      </Card>
      {!dis && <div className="flex justify-end"><Btn data-testid="pn-save" onClick={save}>Simpan</Btn></div>}
    </div>
  );
};
